import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import type { AccountDto, ChainDto } from '../../data/types'
import type { Connection, WalletKind } from '../../core/wallet'
import { Button } from '../../ui/Button'
import { Plus } from 'lucide-react'
import { AccountMenu, walletBalanceText } from './AccountMenu'

/**
 * Loaded on demand, and only ever once.
 *
 * The recovery surface exists for a moment the user chooses to enter, so its
 * copy, its icons, and its detection loop have no business in the bundle every
 * visitor downloads before seeing anything. The initial route is within a
 * kilobyte of its budget; spending that kilobyte here would mean spending it
 * on a sheet most sessions never open.
 */
const WalletRecoverySheet = lazy(() =>
  import('./WalletRecoverySheet').then((module) => ({ default: module.WalletRecoverySheet })),
)

interface Props {
  chain: ChainDto | null
  chainError?: unknown
  walletError?: unknown
  connection: Connection | null
  account: AccountDto | null
  wallets: WalletKind[]
  connecting: boolean
  role?: string | undefined
  onConnect: (kind: WalletKind) => void
  /**
   * Open the application's wallet sheet. When the host owns one — it does on
   * every real route — the bar defers to it instead of raising a second dialog
   * beside the workspace's. Without it the bar still opens its own, so the bar
   * remains testable, and demonstrable, on its own.
   */
  onOpenWallet?: (() => void) | undefined
  onDisconnect: () => void
  onNavigate?: ((path: string) => void) | undefined
}

export function WalletBar({
  chain,
  chainError,
  walletError,
  connection,
  account,
  wallets,
  connecting,
  role,
  onConnect,
  onOpenWallet,
  onDisconnect,
  onNavigate,
}: Props) {
  const canSeeNetworkStatus = role === 'operator' || role === 'admin'
  // One entry point for every wallet outcome. The bar used to branch here into
  // a dead "No wallet extension detected" label, which is the silent failure
  // the plan forbids: the button now always leads somewhere.
  const hostOwnsSheet = Boolean(onOpenWallet)
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const opened = useRef(false)
  const walletName = connection?.name?.trim() || (connection?.wallet === 'leap' ? 'Leap' : 'Keplr')
  const balance = walletBalanceText(account)
  if (recoveryOpen) opened.current = true
  useEffect(() => { if (connection) setRecoveryOpen(false) }, [connection])
  // A failure that nobody opened the sheet to see is still a silent failure —
  // including one from the reconnect the app attempts on its own at load. The
  // host runs the same rule for its own sheet, so this only applies to the
  // fallback one.
  useEffect(() => { if (walletError && !hostOwnsSheet) setRecoveryOpen(true) }, [walletError, hostOwnsSheet])

  return (
    <header className="topbar" aria-label="Network and wallet">
      {canSeeNetworkStatus && <div className="chain-status">
        {chain ? (
          <>
            <span className="chain-id">{chain.chain_id || 'unknown chain'}</span>
            {/* The node being unreachable is shown rather than hidden: the wallet
                config is still valid, so the page works and says what is wrong. */}
            {/* Keyed by height: each new block is a new dot, which rings once (ambience.css). */}
            <span key={chain.reachable ? String(chain.height ?? '') : 'unreachable'} className={chain.reachable ? 'dot dot-ok' : 'dot dot-bad'} />
            <span className="chain-height">
              {chain.reachable ? `height ${chain.height ?? '—'}` : 'node unreachable'}
            </span>
          </>
        ) : chainError ? (
          <span className="wallet-none" role="status" title="The chain configuration could not be loaded">Chain unavailable · retrying</span>
        ) : walletError ? (
          <span className="wallet-none" role="status" title={walletError instanceof Error ? walletError.message : String(walletError)}>Wallet request failed · retry</span>
        ) : (
          <span className="chain-height">loading…</span>
        )}
      </div>}

      <div className="wallet">
        {connection ? (
          <>
            <div className="wallet-credit">
              <div className="wallet-chip" role="group" aria-label="Wallet balance">
                <span className="wallet-chip-amount" title={balance.exact}>
                  <span className="wallet-chip-label">Wallet</span>
                  <span className="wallet-balance">{balance.display}</span>
                </span>
                {/* Secondary on purpose: every page brings its own primary
                    action, and the shell's should never compete with it. */}
                {onNavigate && (
                  <Button variant="secondary" size="sm" className="wallet-add-funds" aria-label="Add funds" onClick={() => onNavigate('/app/billing')}>
                    <Plus size={16} aria-hidden="true" /><span className="wallet-add-funds-label">Add funds</span>
                  </Button>
                )}
              </div>
              {account && !account.exists && (
                <span className="wallet-note" title="This account has never transacted">
                  new account
                </span>
              )}
            </div>
            <AccountMenu connection={connection} account={account} walletName={walletName} onDisconnect={onDisconnect} onNavigate={onNavigate} />
          </>
        ) : (
          <>
            {/* Deliberately not a loading button: progress lives in the sheet,
                and a trigger that renames itself mid-request leaves anyone
                using a screen reader without the control they just pressed. */}
            <Button variant="primary" onClick={onOpenWallet ?? (() => setRecoveryOpen(true))}>
              Connect wallet
            </Button>
            {/* Kept mounted after the first open so closing it still returns
                focus to the trigger rather than dropping it on the document. */}
            {!hostOwnsSheet && opened.current && (
              <Suspense fallback={null}>
                <WalletRecoverySheet
                  open={recoveryOpen}
                  onOpenChange={setRecoveryOpen}
                  wallets={wallets}
                  connecting={connecting}
                  error={walletError}
                  onConnect={onConnect}
                />
              </Suspense>
            )}
          </>
        )}
      </div>
    </header>
  )
}
